import { useMemo, useState } from "react";
import { Image, Button, Tooltip, Checkbox } from "@mantine/core";
import { useCart } from "@/context/CartContext";
import { OrderApi } from "@/api/order.api";
import { ReactComponent as WSPayLogo } from "@/assets/ws-pay/ws-pay-logo.svg";
import MaestroLogo from "@/assets/ws-pay/maestro.gif";
import MasterCardLogo from "@/assets/ws-pay/mastercard.gif";
import VisaLogo from "@/assets/ws-pay/visa.gif";
import VisaSecure from "@/assets/ws-pay/visa-secure.png";
import MastercardIdentityCheck from "@/assets/ws-pay/mastercard-identity-check.png";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import { isItemDateEqualOrAfter } from "@/helpers/issuesAndSubscriptions";
import { config } from "@/config";
import { useMe } from "@/context/MeContext";
import clsx from "clsx";
import "./Cart.css";

const Cart: React.FC = () => {
  const { cart, removeFromCart } = useCart();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { me } = useMe();

  const cartFiltered = useMemo(() => {
    if (cart.some((item) => item.type === "subscription")) {
      return cart.map((item) => {
        if (item.type === "issue" && isItemDateEqualOrAfter(item)) {
          return {
            ...item,
            price: "0.00",
            included: true,
          };
        }
        return { ...item };
      });
    } else return [...cart];
  }, [cart]);

  const isAnyIssueIncludedInSub = useMemo(() => {
    return cartFiltered.some((item) => item.included);
  }, [cartFiltered]);

  const total = cartFiltered
    .reduce((sum, item) => sum + Number(item.price), 0)
    .toFixed(2);

  const totalWithoutVAT = (Number(total) / 1.25).toFixed(2);
  const vatAmount = (Number(total) - Number(totalWithoutVAT)).toFixed(2);

  const handlePayment = async () => {
    const cartData = cartFiltered
      .filter((cartItem) => !cartItem.included)
      .map((cartItem) => ({
        id: cartItem.id,
        price: cartItem.price,
        type: cartItem.type,
      }));
    const order = await OrderApi.createOrder(cartData, total.toString());
    if (order) {
      localStorage.setItem("orderId", order.id.toString());
      //go to ws pay
      let sanitizedAmount = order.price.trim();
      sanitizedAmount = sanitizedAmount.replace(/\./g, ",");
      if (sanitizedAmount.indexOf(",") === -1) {
        sanitizedAmount = `${sanitizedAmount},00`;
      }

      const shopId = config.REACT_APP_WS_PAY_SHOP_ID;
      if (!shopId) return;

      const formData = {
        ShopID: shopId,
        ShoppingCartID: order.id.toString(),
        Version: "2.0",
        TotalAmount: sanitizedAmount,
        Signature: order.signature,
        CustomerFirstName: me?.firstName ?? "",
        CustomerLastName: me?.lastName ?? "",
        CustomerEmail: me?.email ?? "",
        ReturnURL: `${config.REACT_APP_BACKEND_URL}/orders/wspay/success`,
        ReturnErrorURL: `${config.REACT_APP_BACKEND_URL}/orders/wspay/error`,
        CancelURL: `${config.REACT_APP_BACKEND_URL}/orders/wspay/cancel`,
      };
      const submitForm = document.createElement("form");
      submitForm.method = "POST";
      submitForm.action = `${config.REACT_APP_WS_PAY_URL}`;

      Object.entries(formData).map(([key, value]) => {
        const customInput = document.createElement("input");
        customInput.type = "hidden";
        customInput.name = key;
        customInput.value = value;
        submitForm.appendChild(customInput);
      });
      document.body.appendChild(submitForm);
      submitForm.submit();
    }
  };
  return (
    <>
      <div className="cart-main-container">
        <div className="cart-title-container">
          <div className="cart-title">Košarica</div>
        </div>
        <div className="cart-mobile-header">
          <div className="flex gap-3 items-center">
            <div className="text-[#004A99] text-xl font-bold">Košarica</div>
          </div>
        </div>
        <div className="cart-central-container">
          <div className="cart-items">
            {cartFiltered.length ? (
              cartFiltered.map((item, index) => (
                <div key={item.cartId} className="w-full">
                  <div
                    className={clsx(
                      "cart-item-desktop",
                      "cart-item",
                      index === cartFiltered.length - 1 && "cart-last-item"
                    )}
                  >
                    <div className="flex justify-start items-center gap-[1.5rem]">
                      {item.type !== "subscription" && (
                        <Image src={item.cover?.url} className="cart-image" />
                      )}
                      <div className="cart-item-name desk">{item.title}</div>
                    </div>
                    <div className="cart-item-details ">
                      <div className="cart-item-details-group">
                        <div className="cart-item-qty flex-1 min-w-[80px]">
                          Količina: 1
                        </div>
                        <div className="flex-1 min-w-[70px] text-[#004a99] text-sm font-bold">
                          {item.price}€
                          {item.included && (
                            <span className="text-[#fc4a4a]">*</span>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 remove-btn">
                        <Button
                          leftSection={<DeleteIcon />}
                          variant="transparent"
                          color="#525252"
                          onClick={() => removeFromCart(item.cartId)}
                        >
                          Ukloni
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      "cart-item-mobile",
                      "cart-item",
                      index === cartFiltered.length - 1 && "cart-last-item"
                    )}
                  >
                    <div className="flex gap-6 w-full">
                      {item.type !== "subscription" && (
                        <Image src={item.cover?.url} className="cart-image" />
                      )}
                      <div className="cart-item-mobile-right">
                        <div className="cart-item-name mob">{item.title}</div>
                        <div className="text-[#004a99] text-sm font-bold">
                          {" "}
                          {item.price}€
                          {item.included && (
                            <span className="text-[#fc4a4a]">*</span>
                          )}
                        </div>
                        <div className="cart-item-bottom">
                          <div className="cart-item-qty">Količina: 1</div>
                          <div className="remove-btn">
                            <Button
                              className="!py-0"
                              leftSection={<DeleteIcon />}
                              variant="transparent"
                              color="#525252"
                              onClick={() => removeFromCart(item.cartId)}
                            >
                              Ukloni
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col m-[3rem]">Košarica je prazna</div>
            )}
          </div>
          {isAnyIssueIncludedInSub && (
            <div className="info">*Revija je uključena u pretplatu</div>
          )}
          {!!cartFiltered.length && (
            <div className="agree-statement">
              <Checkbox
                checked={agreedToTerms}
                color="#004a99"
                classNames={{
                  input: "!cursor-pointer",
                  label: "!cursor-pointer",
                }}
                label={
                  <div>
                    Potvrđujem da sam upoznat/a s{" "}
                    <span className="!text-[#075985] font-medium">
                      <a className="underline" href="/terms" target="_blank">
                        Općim uvjetima prodaje
                      </a>{" "}
                      i{" "}
                      <a className="underline" href="/privacy" target="_blank">
                        Izjavom o zaštiti osobnih podataka
                      </a>
                    </span>{" "}
                    te da ih u cijelosti prihvaćam
                  </div>
                }
                onChange={(event) =>
                  setAgreedToTerms(event.currentTarget.checked)
                }
              />
            </div>
          )}
          <div className="total-container">
            <div className="p-4 bg-[#fafafa] rounded-xl flex flex-col basis-[65%] gap-4">
              <div className="paying-info-container">
                <div className="flex flex-col flex-1 gap-2">
                  <div className="justify-start text-xs leading-4 font-medium">
                    Plaćanje online kreditnim i debitnim karticama
                  </div>
                  <div className="card-logos-container">
                    <a
                      href="http://www.wspay.info"
                      title="Monri WSpay - Web Secure Payment Gateway"
                      target="_blank"
                    >
                      <WSPayLogo height={30} />
                    </a>
                    <a
                      href="https://www.mastercard.com "
                      title="MasterCard"
                      target="_blank"
                    >
                      <Image h={30} w="auto" src={MasterCardLogo} />
                    </a>
                    <a
                      href="https://www.mastercard.hr/hr-hr/consumers/find-card-products/debit-cards/maestro-debit.html"
                      title="Maestro"
                      target="_blank"
                    >
                      <Image h={30} w="auto" src={MaestroLogo} />
                    </a>
                    <a
                      href="https://www.visaeurope.com"
                      title="Visa"
                      target="_blank"
                    >
                      <Image h={30} w="auto" src={VisaLogo} />
                    </a>
                    <Image h={30} w="auto" src={MastercardIdentityCheck} />
                    <Image h={30} w="auto" src={VisaSecure} />
                  </div>
                </div>
                <div className="flex flex-col flex-1">
                  <div className="self-stretch justify-start text-xs leading-5">
                    <span className="font-medium">
                      Podaci o prodajnom mjestu:
                    </span>
                    <br />
                    CoreEvent d.o.o.
                    <br />
                    Dunjevac 15, 10000 Zagreb,
                    <br />
                    Croatia
                    <br />
                    OIB: 36611335369
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#f4f9ff] rounded-xl flex flex-col basis-[35%] items-end gap-2 h-fit">
              <div className="text-right flex gap-4">
                <span className="text-[#004a99] text-base font-medium">
                  Ukupno:
                </span>
                <Tooltip
                  withArrow
                  color="#004a99"
                  label={
                    <div className="flex flex-col gap-1 text-sm">
                      <div>osnovica: ${totalWithoutVAT}€</div>
                      <div> PDV: {vatAmount}€ (25%)</div>
                    </div>
                  }
                >
                  <span className="text-[#004a99] text-base font-bold cursor-pointer">
                    {total}€
                  </span>
                </Tooltip>
              </div>
              <div className="flex !w-full">
                <Button
                  variant="filled"
                  color="#004a99"
                  radius="xl"
                  size="lg"
                  className="payment-button flex-1 h-[3.25rem]"
                  disabled={!cartFiltered.length || !agreedToTerms}
                  onClick={handlePayment}
                >
                  Nastavi na plaćanje
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
